import { appConfigIds } from "@constants/common";
import type { ProethicaModuleSummary } from "@constants/demo/session-collection/proethica";
import { getHubTokenHeader } from "@helpers/lib/hub-auth";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { SessionAnalysis, SessionAnalysisCard } from "@memorang/features";
import { Box, useDevice } from "@memorang/ui";
import { useLocalSearchParams } from "expo-router";
import type { ComponentProps } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Apps } from "types/apps";
import { ModuleSummaryDialog } from "./ModuleSummaryDialog";

const STRIP_CONTENT_HEIGHT = 120;

export type SessionAnalysisProps = Omit<
	ComponentProps<typeof SessionAnalysis>,
	| "cardTitle"
	| "cardSubtitle"
	| "cardIcon"
	| "cardContentHeight"
	| "cardActionLabel"
	| "cardContainerStyle"
	| "cardMode"
>;

const SummaryStrip = ({
	moduleSummary,
}: {
	moduleSummary: ProethicaModuleSummary;
}) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [showSummaryDialog, setShowSummaryDialog] = useState(false);
	return (
		<>
			<SessionAnalysisCard
				sessionAnalysis={moduleSummary.text}
				title={t("report.proethica.summary.title")}
				subtitle={t("report.proethica.summary.subtitle")}
				icon="book-open-variant"
				contentHeight={STRIP_CONTENT_HEIGHT}
				actionLabel={t("report.proethica.summary.action")}
				toggleShowCompleteReport={() => setShowSummaryDialog(true)}
				containerStyle={{ backgroundColor: theme.colors.elevation.level2 }}
				cardMode="contained"
			/>
			<ModuleSummaryDialog
				visible={showSummaryDialog}
				text={moduleSummary.text}
				handleClose={() => setShowSummaryDialog(false)}
			/>
		</>
	);
};

const FeedbackStrip = ({
	sessionAnalysisProps,
	additionalContext,
	reportsReady,
}: {
	sessionAnalysisProps: SessionAnalysisProps;
	additionalContext?: string;
	reportsReady: boolean;
}) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const appId = useAppStore((store) => store.app?.id);
	// The report shell is still mocked (app-level isMockedData), so
	// sessionAnalysisProps carries a mock session id. The live analysis must use
	// the real backend session id from the route.
	const { id: routeSessionId } = useLocalSearchParams<{ id: string }>();
	return (
		<SessionAnalysis
			{...sessionAnalysisProps}
			sessionId={Number(routeSessionId)}
			appId={appId}
			examId={appConfigIds[Apps.Proethica].collectionId}
			getAuthHeaders={getHubTokenHeader}
			ready={reportsReady}
			additionalContext={additionalContext}
			cardTitle={t("report.proethica.feedback.title")}
			cardSubtitle={t("report.proethica.feedback.subtitle")}
			cardIcon="lightbulb-on-outline"
			cardContentHeight={STRIP_CONTENT_HEIGHT}
			cardActionLabel={t("report.proethica.feedback.action")}
			cardContainerStyle={{ backgroundColor: theme.colors.elevation.level2 }}
			cardMode="contained"
		/>
	);
};

// Module 1 is the content-only intro (no scored/AI work), so it has no
// personalized feedback to pair with the summary - the summary then takes the
// full width rather than leaving an empty slot.
export const InsightStrips = ({
	isFirstModule,
	moduleSummary,
	sessionAnalysisProps,
	additionalContext,
	reportsReady,
}: {
	isFirstModule: boolean;
	moduleSummary?: ProethicaModuleSummary;
	sessionAnalysisProps: SessionAnalysisProps;
	additionalContext?: string;
	reportsReady: boolean;
}) => {
	const { isMobile } = useDevice();
	const stacked = isMobile || isFirstModule;
	return (
		<Box
			flexDirection={stacked ? "column" : "row"}
			alignItems="stretch"
			style={{ gap: 8 }}
		>
			{moduleSummary ? (
				<Box flex={stacked ? undefined : 1}>
					<SummaryStrip moduleSummary={moduleSummary} />
				</Box>
			) : null}
			{isFirstModule ? null : (
				<Box flex={stacked ? undefined : 1}>
					<FeedbackStrip
						sessionAnalysisProps={sessionAnalysisProps}
						additionalContext={additionalContext}
						reportsReady={reportsReady}
					/>
				</Box>
			)}
		</Box>
	);
};
